/* ---------------------------------------------------------------------------
   Miniweight — LP Pack Minceur (Meta paid traffic)

   Responsibilities, deliberately narrow:
     1. Keep the CTA price in sync with the selected cure.
     2. Prevent double submissions and show a loading state.
     3. After the theme's native `variant:add`, go straight to checkout.
     4. Show/hide the mobile sticky CTA.
     5. Smooth-scroll the CTAs to the cure selector.

   It adds no tracking, fires no analytics events, and issues no cart or
   product requests of its own. If this file fails to load, the buy box still
   works: the cure radios are the Shopify variant input (name="id") and the
   form posts natively to /cart/add.
--------------------------------------------------------------------------- */
(function () {
  'use strict';

  var root = document.querySelector('[data-mw-pack-lp]');
  if (!root) return;

  var form = root.querySelector('form[action*="/cart/add"]');
  var submitBtn = root.querySelector('[data-mwplp-submit]');
  var ctaPrice = root.querySelector('[data-mwplp-cta-price]');
  var errorBox = root.querySelector('[data-mwplp-error]');
  var ctaWrap = root.querySelector('.mwplp-buy__cta');
  var klarnaPlacement = root.querySelector('[data-mwplp-klarna-placement]');
  var sticky = root.querySelector('[data-mwplp-sticky]');
  var buyBox = root.querySelector('#mwplp-cures');
  var tiers = Array.prototype.slice.call(root.querySelectorAll('[data-mwplp-tier]'));
  var radios = Array.prototype.slice.call(root.querySelectorAll('.mwplp-tier__radio'));

  var submitting = false;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* --- 1. selection <-> CTA sync ----------------------------------------- */

  function checkedRadio() {
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].checked && !radios[i].disabled) return radios[i];
    }
    return null;
  }

  function syncSelection() {
    var checked = checkedRadio();

    tiers.forEach(function (tier) {
      var radio = tier.querySelector('.mwplp-tier__radio');
      var isSelected = !!(radio && checked && radio === checked);
      tier.setAttribute('data-selected', isSelected ? 'true' : 'false');
    });

    if (checked) {
      var formatted = checked.getAttribute('data-mwplp-price-formatted');

      if (ctaPrice && formatted) ctaPrice.textContent = formatted;

      /* Klarna's placement prices the selected cure, not a fixed amount. */
      if (klarnaPlacement) {
        var cents = checked.getAttribute('data-mwplp-price');
        if (cents && klarnaPlacement.getAttribute('data-purchase-amount') !== cents) {
          klarnaPlacement.setAttribute('data-purchase-amount', cents);
          try {
            if (window.Klarna && window.Klarna.OnsiteMessaging) {
              window.Klarna.OnsiteMessaging.refresh();
            }
          } catch (e) {
            /* Klarna is a nicety here, never a failure */
          }
        }
      }
    }

    if (submitBtn) {
      var none = !checked;
      submitBtn.disabled = none || submitting;
      if (none) {
        submitBtn.setAttribute('aria-disabled', 'true');
      } else {
        submitBtn.removeAttribute('aria-disabled');
      }
    }

    hideError();
  }

  radios.forEach(function (radio) {
    radio.addEventListener('change', syncSelection);
  });

  /* Clicking anywhere on a disabled card should not look broken. */
  tiers.forEach(function (tier) {
    tier.addEventListener('click', function (event) {
      var radio = tier.querySelector('.mwplp-tier__radio');
      if (radio && radio.disabled) {
        event.preventDefault();
      }
    });
  });

  /* --- 2. loading + double-submit protection ----------------------------- */

  function setLoading(on) {
    submitting = on;
    if (!submitBtn) return;

    /*
      DO NOT set aria-busy on the submit button here.

      The theme's <product-form> handler starts with:
          if (event.submitter?.getAttribute("aria-busy") === "true") return;
      Our submit listener runs first (capture phase on document), so marking the
      button busy would make the theme bail out before it ever calls
      /cart/add.js - the button would spin forever and nothing would happen.
      The theme sets and clears aria-busy on the button itself while its request
      is in flight. We mark the surrounding region busy instead.
    */
    if (ctaWrap) ctaWrap.setAttribute('aria-busy', on ? 'true' : 'false');

    if (on) {
      submitBtn.setAttribute('data-loading', 'true');
      submitBtn.disabled = true;
    } else {
      submitBtn.removeAttribute('data-loading');
      submitBtn.removeAttribute('aria-busy');
      submitBtn.disabled = !checkedRadio();
    }
  }

  function showError(message) {
    if (!errorBox) return;
    errorBox.textContent = message;
    errorBox.hidden = false;
  }

  function hideError() {
    if (!errorBox) return;
    errorBox.hidden = true;
    errorBox.textContent = '';
  }

  var GENERIC_ERROR =
    "Nous n'avons pas pu ajouter votre cure. Vérifiez votre connexion et réessayez dans un instant.";

  if (form) {
    /*
      Capture on `document` so this runs before the theme's own submit handler
      on the form. That is what lets a duplicate submission be stopped before
      a second /cart/add.js request is issued.
    */
    document.addEventListener(
      'submit',
      function (event) {
        if (event.target !== form) return;

        if (submitting) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        if (!checkedRadio()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          showError('Choisissez d’abord votre cure.');
          return;
        }

        hideError();
        setLoading(true);
      },
      true
    );

    /* --- 3. native success -> checkout ----------------------------------- */

    form.addEventListener('variant:add', function () {
      var target = root.getAttribute('data-mwplp-checkout-url') || '/checkout';
      try {
        if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
          target = window.Shopify.routes.root + 'checkout';
        }
      } catch (e) {
        /* keep the Liquid-rendered fallback */
      }
      window.location.assign(target);
    });

    form.addEventListener('cart:error', function (event) {
      setLoading(false);
      var message = (event && event.detail && event.detail.error) || GENERIC_ERROR;
      showError(message);
    });
  }

  /*
    Coming back via the back button (bfcache) must never restore a button that
    is stuck in its loading state.
  */
  window.addEventListener('pageshow', function () {
    setLoading(false);
    syncSelection();
    seedSticky();
  });

  /* --- 4. mobile sticky CTA --------------------------------------------- */

  var pastHero = false;
  var buyVisible = false;
  var heroCta = root.querySelector('.mwplp-hero .mwplp-btn');

  function updateSticky() {
    if (!sticky) return;
    sticky.setAttribute('data-hidden', pastHero && !buyVisible ? 'false' : 'true');
  }

  function onScreenFraction(el) {
    var b = el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var visible = Math.min(b.bottom, vh) - Math.max(b.top, 0);
    var reference = Math.min(b.height, vh) || 1;
    return Math.max(0, visible) / reference;
  }

  /*
    Seed the state from geometry rather than waiting for the first observer
    callback. This is what makes the bar correct when the page opens already
    scrolled - an in-page anchor, or a restored scroll position on back/forward.
  */
  function seedSticky() {
    if (!sticky || !buyBox) return;
    pastHero = heroCta ? heroCta.getBoundingClientRect().bottom <= 0 : true;
    buyVisible = onScreenFraction(buyBox) > 0.12;
    updateSticky();
  }

  if (sticky && buyBox && 'IntersectionObserver' in window) {
    if (heroCta) {
      new IntersectionObserver(
        function (entries) {
          pastHero = !entries[entries.length - 1].isIntersecting;
          updateSticky();
        },
        { threshold: 0 }
      ).observe(heroCta);
    } else {
      pastHero = true;
    }

    /*
      Hide the bar once a meaningful part of the buy box is on screen, so it
      never sits on top of the real buy button or the legal links.
    */
    new IntersectionObserver(
      function (entries) {
        buyVisible = entries[entries.length - 1].isIntersecting;
        updateSticky();
      },
      { threshold: 0.12 }
    ).observe(buyBox);

    seedSticky();
  } else if (sticky) {
    sticky.setAttribute('data-hidden', 'false');
  }

  /* --- 5. anchor scrolling ---------------------------------------------- */

  root.addEventListener('click', function (event) {
    var trigger = event.target.closest ? event.target.closest('[data-mwplp-scroll]') : null;
    if (!trigger || !buyBox) return;

    event.preventDefault();
    buyBox.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start'
    });

    /* Move keyboard focus with the scroll so the selector is operable. */
    var firstEnabled = checkedRadio() || radios[0];
    if (firstEnabled) {
      window.setTimeout(function () {
        try {
          firstEnabled.focus({ preventScroll: true });
        } catch (e) {
          /* focus is a nicety, never a failure */
        }
      }, prefersReducedMotion() ? 0 : 420);
    }
  });

  syncSelection();
})();
